import Swiper from 'swiper';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { Alert, App, Button, Popconfirm, Progress } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Swiper as SwiperReact, SwiperSlide } from 'swiper/react';
import { Routes } from '@/routes';
import { Flex } from '@/components';
import { useForceUpdate } from '@/hooks';
import { useSurveyStore } from '@/store';
import { SurveyHttp } from '@/services';
import { useSurveyParticipateContext } from './contexts';
import { hasEmptyAnswer, hasEmptyMandatoryAnswer, hasSelectedCountViolated } from './functions';
import SurveyQuestionChoice from './SurveyQuestionChoice';
import QuestionInfo from './QuestionInfo';

type ProgressType = 'success' | 'warn' | 'error';
const BOX_CLASSNAME = 'bg-gray-100 rounded-xl p-3 lg:p-4 border border-solid border-[#ddd] h-full';

export interface SurveyQuestions {
  survey: SurveyEntity;
  questions: SurveyQuestionMultipleChoiceEntity[];
}

export function SurveyQuestions({ survey, questions }: SurveyQuestions) {

  const navigate = useNavigate();
  const { message } = App.useApp();
  const forceUpdate = useForceUpdate();
  const swiperRef = useRef<Swiper>(null);
  const { fetchSurveys } = useSurveyStore();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const { answers } = useSurveyParticipateContext();
  const [t] = useTranslation(['common', 'survey']);

  const defaultPercent = useMemo(() => {
    const tmp = 100 / questions.length;
    return tmp - (tmp / 2);
  }, [questions]);

  const onSlideChange = useCallback((swiper: Swiper) => {
    setProgress(swiper.progress === 0 ? defaultPercent : swiper.progress * 100);
  }, [defaultPercent]);

  const initSwiper = useCallback((swiper: Swiper) => {
    swiperRef.current = swiper;
    forceUpdate();
  }, [forceUpdate]);

  const onPrev = useCallback(() => {
    swiperRef.current.slidePrev();
  }, [swiperRef]);

  const onNext = useCallback(() => {
    swiperRef.current.slideNext();
  }, [swiperRef]);

  const onReachEnd = useCallback(() => {
    setReachedEnd(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      await SurveyHttp.answerSurvey(survey.id, answers);
      message.success(t('common:messages.save-info'));
      navigate(Routes.Home);
    }
    catch (e) {
      console.log('e', e);
    }
    finally {
      setLoading(false);
      fetchSurveys();
    }
  }, [answers, survey, message, t, navigate, fetchSurveys]);

  const isBeginning = swiperRef?.current?.isBeginning;
  const isEnd = swiperRef?.current?.isEnd;

  const mandatoryViolated = hasEmptyMandatoryAnswer(questions, answers);
  const emptyViolated = hasEmptyAnswer(questions, answers);
  const selectedCountViolated = hasSelectedCountViolated(questions, answers);

  let status: ProgressType = 'success';
  let statusMessage: string = undefined;

  if (mandatoryViolated) {
    status = 'error';
    statusMessage = t('survey:errors.mandatory-questions-not-answered');
  }
  else if (reachedEnd && selectedCountViolated) {
    status = 'error';
    statusMessage = selectedCountViolated;
  }
  else if (emptyViolated) {
    status = 'warn';
    statusMessage = t('survey:errors.some-questions-not-answered');
  }

  return (
    <Flex className="rtl" gap={0} direction="column">
      <SwiperReact
        dir="rtl"
        watchSlidesProgress
        autoplay={false}
        spaceBetween={50}
        slidesPerView={1}
        allowTouchMove={false}
        onSwiper={initSwiper}
        onReachEnd={onReachEnd}
        onSlideChange={onSlideChange}
      >
        {questions.length === 0 && (
          <SwiperSlide
            key="no-question"
            className={classNames('min-h-[200px] text-center', BOX_CLASSNAME)}>
            <Alert type="warning" message={t('survey:messages.no-question-defined')} />
          </SwiperSlide>
        )}
        {questions.map(q => (
          <SwiperSlide key={q.id} className="w-full">
            <Flex gap={12} direction="column" className={BOX_CLASSNAME}>
              <p className="my-0 mb-2 text-mid font-medium text-[#444]">
                {q.mandatory && <span className="text-red-500">{'* '}</span>}
                {q.title ? q.title : t('survey:messages.question-text-not-set')}
              </p>
              {q.choices?.map((choice, i) => (
                <SurveyQuestionChoice
                  index={i}
                  question={q}
                  key={choice.id}
                  choice={choice}
                />
              ))}
              <Flex direction="column" className="mt-auto">
                <QuestionInfo question={q} />
              </Flex>
            </Flex>
          </SwiperSlide>
        ))}
      </SwiperReact>
      <Progress
        type="line"
        showInfo={false}
        percent={progress === 0 ? defaultPercent : progress}
        status={(isEnd || reachedEnd) && status === 'error' ? 'exception' : undefined}
        strokeColor={status === 'warn' && reachedEnd ? 'orange' : undefined}
      />
      <AnimatePresence mode="wait">
        {(status === 'warn' && reachedEnd) &&
          <motion.div key="answer-warn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} >
            <Alert
              type="warning"
              className="mb-4"
              message={statusMessage}
            />
          </motion.div>
        }
        {(status === 'error' && reachedEnd) &&
          <motion.div key="answer-err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} >
            <Alert
              type="error"
              className="mb-4"
              message={statusMessage}
            />
          </motion.div>
        }
      </AnimatePresence>
      <Flex>
        <Button disabled={isBeginning} onClick={onPrev} size="middle">{t('survey:labels.prev-question')}</Button>
        <Button disabled={isEnd} onClick={onNext} size="middle">{t('survey:labels.next-question')}</Button>
        <Popconfirm
          onConfirm={handleSubmit}
          title={t('survey:messages.confirm-form')}>
          <Button
            size="middle"
            type="primary"
            className="ms-auto"
            loading={loading}
            disabled={!isEnd || mandatoryViolated}
          >
            {t('common:labels.confirm-submit')}
          </Button>
        </Popconfirm>
      </Flex>
    </Flex>
  );
}

export default SurveyQuestions;
